'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef } from 'react';

import { useLocationSearch } from '../../hooks/useLocationSearch';
import { MemoizedLocationSearchInput } from './MemoizedLocationSearchInput';
import { CurrentLocationButton, RecentLocationsList } from './index';
import type { RecentLocation } from './types';

interface LocationPickerContentProps {
  onLocationSelect: (location: { latitude: number; longitude: number; name: string }) => void;
}

export const LocationPickerContent: React.FC<LocationPickerContentProps> = ({ onLocationSelect }) => {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    recentLocations,
    isLoadingCurrentLocation,
    getCurrentLocation,
    saveRecentLocation
  } = useLocationSearch();

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleUseCurrentLocation = async () => {
    const location = await getCurrentLocation();
    if (location) {
      onLocationSelect(location);
    }
  };

  const handleLocationSelect = (location: RecentLocation) => {
    saveRecentLocation(location);
    onLocationSelect(location);
  };

  const isShowingSearchResults = searchQuery.trim().length >= 2;
  const locationsToShow = isShowingSearchResults ? searchResults : recentLocations;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 py-4">
        <MemoizedLocationSearchInput
          inputRef={inputRef}
          placeholder={t('SearchPage.searchLocation')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      <CurrentLocationButton onClick={handleUseCurrentLocation} disabled={isLoadingCurrentLocation} />
      <div className="w-full h-px bg-neutral-200 my-2"></div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isSearching ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
          </div>
        ) : locationsToShow.length > 0 ? (
          <RecentLocationsList
            locations={locationsToShow}
            onLocationSelect={handleLocationSelect}
            headerText={isShowingSearchResults ? t('SearchPage.searchResults') : undefined}
          />
        ) : searchQuery.trim().length >= 2 ? (
          <div className="py-8 flex items-center justify-center text-center text-neutral-500">
            {t('Map.noLocationsToDisplay')}
          </div>
        ) : (
          <div className="py-8 flex items-center justify-center text-center text-neutral-500">
            {t('Map.noRecentLocations')}
          </div>
        )}
      </div>
    </div>
  );
};
